import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Divider from "@mui/material/Divider";
import React from "react";
import { ReactComponent as CalendarDateIcon } from "../../assets/images/calendar-date-icon.svg";
import { ReactComponent as LayersIcon } from "../../assets/images/layers-icon.svg";
import { Category } from "../../helpers/types/common";
import { getValueOrBlankArray } from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { MuiBox } from "../box/MuiBox";
import { MuiButtonTertiary } from "../button/MuiButton";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import {
  MuiOptionMenu,
  OptionMenuItemProps,
} from "../optionMenu/MuiOptionMenu";
import { StatusBadge, StatusBadgeType } from "../statusBadge/StatusBadge";
import { MuiTypography } from "../typography/MuiTypography";
import { StyledItemDetailRow } from "./ItemDetailRow.style";

interface ItemDetailRowProps {
  name: string;
  date: string;
  category: Category;
  status: StatusBadgeType | undefined;
  statusLabel: string;
  menuItems?: OptionMenuItemProps[];
  onItemRowDetailClick?: () => void;
  navigationButtonAriaLabel?: string;
  showItemNavigationIcon?: boolean;
}

const IconText: React.FC<{
  icon: React.ReactNode;
  text: string;
}> = React.memo(({ icon, text }) => (
  <MuiBox className="icon-text-wrapper">
    <MuiBox className="icon-box">{icon}</MuiBox>
    <MuiTypography variant="body2">{text}</MuiTypography>
  </MuiBox>
));

const ItemDetailRowComponent: React.FC<ItemDetailRowProps> = ({
  name,
  date,
  category,
  status,
  statusLabel,
  onItemRowDetailClick,
  menuItems,
  navigationButtonAriaLabel = "View details",
  showItemNavigationIcon = true,
}) => {
  const { isMobile } = useMediaQueries();
  const renderType = () => {
    if (!category?.categoryName) return null;

    return (
      <>
        <Divider orientation="vertical" flexItem className="divider" />
        <IconText icon={<LayersIcon />} text={category.categoryName} />
      </>
    );
  };

  const renderStatusBadge = () => {
    if (!status || !statusLabel) return null;

    return (
      <MuiBox className="status-badge-mobile-wrapper">
        <StatusBadge status={status} label={statusLabel} />
      </MuiBox>
    );
  };

  // MOBILE VIEW
  if (isMobile) {
    return (
      <StyledItemDetailRow className="item-row-detail-wrapper">
        <MuiBox className="line-wrapper" />
        <MuiBox className="content-wrapper">
          {/* Row 1: Name + Menu + Arrow */}
          <MuiBox className="mobile-top-row">
            <MuiEllipseLabel
              text={name}
              labelTypoVariant="h3SemiBold"
              labelTypoClassName={`title-text ${showItemNavigationIcon ? "clickable" : ""}`}
              onClick={
                showItemNavigationIcon ? onItemRowDetailClick : undefined
              }
            />
            <MuiBox className="mobile-actions">
              <MuiOptionMenu
                menuItems={getValueOrBlankArray(menuItems)}
                shouldRenderEmpty
              />
              {showItemNavigationIcon && (
                <MuiButtonTertiary
                  onClick={onItemRowDetailClick}
                  aria-label={navigationButtonAriaLabel}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </MuiButtonTertiary>
              )}
            </MuiBox>
          </MuiBox>
          <IconText icon={<CalendarDateIcon />} text={date} />
          {renderType()}
          {renderStatusBadge()}
        </MuiBox>
      </StyledItemDetailRow>
    );
  }

  //  DESKTOP VIEW (unchanged)
  return (
    <StyledItemDetailRow className="item-row-detail-wrapper">
      <MuiBox className="line-wrapper" />
      <MuiBox className="content-wrapper">
        <MuiBox className="info-section">
          <MuiEllipseLabel
            text={name}
            labelTypoVariant="h3SemiBold"
            labelTypoClassName={`title-text ${showItemNavigationIcon ? "clickable" : ""}`}
            onClick={showItemNavigationIcon ? onItemRowDetailClick : undefined}
          />
          <MuiBox className="left-stack-container">
            <IconText icon={<CalendarDateIcon />} text={date} />
            {renderType()}
          </MuiBox>
        </MuiBox>

        <MuiBox className="right-section">
          <MuiBox className="status-badge-wrapper">
            {renderStatusBadge()}
          </MuiBox>
          <MuiOptionMenu
            menuItems={getValueOrBlankArray(menuItems)}
            shouldRenderEmpty
          />
          {showItemNavigationIcon && (
            <MuiButtonTertiary
              onClick={onItemRowDetailClick}
              aria-label={navigationButtonAriaLabel}
              className="btn-tertiary"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </MuiButtonTertiary>
          )}
        </MuiBox>
      </MuiBox>
    </StyledItemDetailRow>
  );
};

export const ItemDetailRow = React.memo(ItemDetailRowComponent);
export default ItemDetailRow;
